import { LyfeTooltip, Typography } from "@lyfegen-platform/react-common";
import { isEmptyValue } from "@lyfegen-platform/utils";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Collapse,
	Divider,
	IconButton,
	Theme,
	Tooltip,
	withStyles,
} from "@material-ui/core";
import { InfoOutlined, Undo } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import CallMadeIcon from "@material-ui/icons/CallMade";
import ClearIcon from "@material-ui/icons/Clear";
import MailOpenIcon from "@material-ui/icons/DraftsOutlined";
import MailClosedIcon from "@material-ui/icons/MailOutlineOutlined";
import LyfeAvatar from "../../../../components/LyfeAvatar";
import { dataModuleString, useDataMutation } from "../../../../hooks/useDataModule";
import { QuestsType } from "../../../../models/notifications";
import { notificationInfoState, questState } from "../../../../store/layout";
import { userPersonalInfoState } from "../../../../store/user";
import { QuestCompletionStatus, TaskStatus } from "../../../../models/generated/graphql";
import LyfeButton from "libs/react-common/src/Button/LyfeButton";
import { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useTimer } from "react-timer-hook";
import { useRecoilState, useSetRecoilState } from "recoil";
import renderCurrency from "../../../../utils/currency";
import { GET_QUEST_FILTERS, GET_RECORD_FILTERS, UPDATE_QUEST_FILTER, UPDATE_RECORD_FILTER } from "../../../data/queries";
import formatDateString from "../../../home/format-date-string";
import tasksStyles from "../tasks/inbox-tasks-styles";
import { InboxQuestComponentProps, InboxQuestContentPayload } from "./map-inbox-quests";
import { CHANGE_QUEST_STATUS, READ_OR_UNREAD_QUEST, REMOVE_QUEST_OWNERSHIP, TAKE_QUEST_OWNERSHIP } from "./queries";
import DeletionCountDown from "../../../../components/DeletionCountDown";

export const NotificationTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: (theme.palette.toolTipBg as any)?.main || "black",
		color: theme.palette.common.white,
		maxWidth: 346,
		fontSize: 12,
		fontWeight: 600,
	},
}))(Tooltip);

export function formatQuestContent(triggerEventId: number, content: InboxQuestContentPayload, t: (key: string) => any) {
	const { contentSeparator } = tasksStyles();
	switch (triggerEventId) {
		case QuestsType.UncoveredClaims:
			return (
				<Typography type="inherit">
					{t("uncovered-claims")}
					<span className={contentSeparator} />
					{renderCurrency(content.value, content.currency, 2, 2)}
				</Typography>
			);
		case QuestsType.PotentialDiscontinuation:
			return (
				<Typography type="inherit">
					{t("potential-discontinuation")}
					<span className={contentSeparator} />
					{renderCurrency(content.value, content.currency, 2, 2)}
				</Typography>
			);
		case QuestsType.UnmatchedRecords:
			return (
				<Typography type="inherit">
					{t("unmatched-records")}
					<span className={contentSeparator} />
					{renderCurrency(content.value, content.currency, 2, 2)}
				</Typography>
			);
		default:
			return <></>;
	}
}

export function formatExpansionQuestContent(
	triggerEventId: number,
	content: InboxQuestContentPayload,
	t: (key: string, options?: any) => any
) {
	switch (triggerEventId) {
		case QuestsType.UncoveredClaims:
			return (
				<>
					<Typography style={{ fontSize: 14, fontWeight: 600, padding: "0px 0" }}>
						{t("claims-outside-ca-description-flexible", {
							caseId: content.caseId,
							patientId: content.patientId,
							therapyDecision: content.therapyDecision ?? "--",
						})}{" "}
						<Typography style={{ fontSize: 14, fontWeight: 600, padding: "0px 0" }}>
							{`${t("claims-outside-ca-description")} `}
						</Typography>
					</Typography>
				</>
			);
		case QuestsType.PotentialDiscontinuation:
			return (
				<>
					<Typography style={{ fontSize: 14, fontWeight: 600, padding: "10px 0" }}>
						{t(`potential-discontinuation-description-flexible`, {
							caseId: content.caseId,
							patientId: content.patientId,
							value: content.threshold,
						})}
						<Typography style={{ fontSize: 14, fontWeight: 600, padding: "10px 0" }}>
							{`${t("potential-discontinuation-description")} `}
						</Typography>{" "}
					</Typography>{" "}
				</>
			);
		case QuestsType.UnmatchedRecords:
			return (
				<>
					<Typography style={{ fontSize: 14, fontWeight: 600, padding: "10px 0" }}>
						{t(`unmatched-records-description-flexible`, {
							value: renderCurrency(content.value || 0, content.currency),
							brandName: content.brandName || "",
						})}
						<Typography style={{ fontSize: 14, fontWeight: 600, padding: "10px 0" }}>
							{`${t("unmatched-records-description")} `}
						</Typography>{" "}
					</Typography>{" "}
				</>
			);
		default:
			return <></>;
	}
}

const rowStyle = (index: number, isHovering: boolean) => {
	if (index % 2 === 0) {
		return { backgroundColor: isHovering ? "#fcfcfc" : "#fff" };
	} else {
		return { backgroundColor: isHovering ? "rgba(126, 166, 180, 0.15)" : "rgba(126, 166, 180, 0.08)" };
	}
};

const InboxQuestContent: React.FC<InboxQuestComponentProps> = ({
	triggerEventId,
	content,
	questDate,
	isVisible,
	questCompletionStatus,
	questStatus,
	questId,
	isAssignee,
	isAutoCompleted,
	questOwner,
	resolvedBy,
	rowIndex,
	setItemSize,
	onUpdate,
}) => {
	const {
		goToTaskContainer,
		goToTaskContainerWrap,
		questContent,
		isReadDot,
		iconButton,
		iconQuest,
		expandedQuestContent,
		accordionSummary,
		task,
	} = tasksStyles();
	const [temQuestStatus, setQuestStatus] = useState(questStatus && questStatus === TaskStatus.Unread);
	const { t } = useTranslation();
	const { push } = useHistory();
	const [mutate] = useDataMutation(READ_OR_UNREAD_QUEST);
	const setOpenNotificationPopup = useSetRecoilState(notificationInfoState);
	const [tempQuestOwner, setQuestOwner] = useState(questOwner);
	const [questCompleted, setQuestCompleted] = useState(questCompletionStatus);
	const [updateFilter] = useDataMutation(UPDATE_RECORD_FILTER);
	const [updateQuestFilter] = useDataMutation(UPDATE_QUEST_FILTER);
	const duration = 5000;
	const expiryTimestamp = new Date(Date.now() + duration);
	const { seconds, isRunning, start, restart } = useTimer({ expiryTimestamp: expiryTimestamp, autoStart: false });
	const [mutation] = useDataMutation(CHANGE_QUEST_STATUS);
	const [takeOwnershipMutation] = useDataMutation(TAKE_QUEST_OWNERSHIP);
	const [removeQuestOwnershipMutation] = useDataMutation(REMOVE_QUEST_OWNERSHIP);
	const [user, setUserData] = useRecoilState(userPersonalInfoState);
	const [expandedQuest, setExpandedQuest] = useRecoilState(questState);
	const [show, setShow] = useState(false);
	const expandedSize = triggerEventId === QuestsType.UncoveredClaims ? 222 : 275;

	const updateSize = () => {
		setItemSize(rowIndex, expandedQuest.questId === questId ? expandedSize : 40);
	};

	useEffect(() => {
		updateSize();
	}, [expandedQuest]);

	const handleClickDelete = () => {
		!isRunning ? start() : restart(expiryTimestamp, false);
	};

	const handleRestart = (seconds: number) => {
		if (seconds) return;
		restart(expiryTimestamp, false);
		mutation({
			variables: {
				questId: questId,
				questCompletionStatus: QuestCompletionStatus.Dismissed,
				ownerId: user.id,
			},
			onCompleted: () => {
				setQuestOwner(user);
				setQuestCompleted(QuestCompletionStatus.Dismissed);
				onUpdate(rowIndex, { questCompletionStatus: QuestCompletionStatus.Dismissed, questOwner: user });
			},
		});
	};

	function goToQuestHandle(e: MouseEvent, triggerEventId: number) {
		e.stopPropagation();
		setOpenNotificationPopup(false);

		if (temQuestStatus) {
			markQuestAsReadOrUnread(e);
		}

		switch (triggerEventId) {
			case QuestsType.UncoveredClaims:
			case QuestsType.PotentialDiscontinuation:
				push(`/cases/case?id=${content.caseId}`);
				return;
			case QuestsType.UnmatchedRecords:
				updateFilter({
					variables: {
						req: JSON.stringify({
							status: ["UNMATCHED"],
							brandNames: [[content.brandName]],
						}),
					},
					refetchQueries: [{ context: { module: dataModuleString }, query: GET_RECORD_FILTERS }],
				});
				updateQuestFilter({
					variables: {
						req: JSON.stringify({}),
					},
					refetchQueries: [{ context: { module: dataModuleString }, query: GET_QUEST_FILTERS }],
					onCompleted: () => {
						push(`/data/records/quests?id=${questId}`);
					},
				});

				return;
		}
	}

	function getHoverText() {
		if (questCompleted === QuestCompletionStatus.Dismissed) {
			return "quest-dismisser";
		}

		if (questCompleted === QuestCompletionStatus.SubmittedForm) {
			return "quest-resolver";
		}

		if (questCompleted === QuestCompletionStatus.Revoked) {
			return "quest-revoked-by";
		}

		return "quest-owner";
	}

	function getUserOwner() {
		if (
			questCompleted &&
			[QuestCompletionStatus.SubmittedForm, QuestCompletionStatus.Dismissed, QuestCompletionStatus.Revoked].includes(questCompleted)
		) {
			return resolvedBy?.firstName + " " + resolvedBy?.lastName;
		}

		return tempQuestOwner?.firstName + " " + tempQuestOwner?.lastName;
	}

	function markQuestAsReadOrUnread(event: React.MouseEvent, goToEvent?: boolean) {
		event.stopPropagation();
		if (expandedQuest.questId === questId) {
			return;
		}

		const currentQuestStatus = !temQuestStatus && (isEmptyValue(goToEvent) || !goToEvent);
		mutate({
			variables: {
				questId: questId,
				questStatus: currentQuestStatus ? TaskStatus.Unread : TaskStatus.Read,
			},
			onCompleted: () => {
				setQuestStatus((prev) => !prev);
				if (isAssignee) {
					setUserData((prev) => {
						const currentQuestsCount = prev.questsCount + (currentQuestStatus ? 1 : -1);
						return { ...prev, questsCount: currentQuestsCount > 0 ? currentQuestsCount : 0 };
					});
				}
				onUpdate(rowIndex, { questStatus: currentQuestStatus ? TaskStatus.Unread : TaskStatus.Read });
			},
		});
	}

	function handleOwnership(event: React.MouseEvent) {
		if (questCompleted !== QuestCompletionStatus.Active || (tempQuestOwner && tempQuestOwner.id !== user.id)) return;

		event.stopPropagation();
		if (!tempQuestOwner) {
			setQuestOwner(user);
			onUpdate(rowIndex, { questOwner: user });
			takeOwnershipMutation({
				variables: {
					questId: questId,
					ownerId: user.id,
				},
			});
			return;
		}
		setQuestOwner(null);
		onUpdate(rowIndex, { questOwner: null });
		removeQuestOwnershipMutation({
			variables: {
				questId: questId,
				ownerId: user.id,
			},
		});
	}

	function toggleExpanded(e: any, expandedQuestId: string) {
		setExpandedQuest((prev) => {
			return { questId: prev.questId === questId ? "" : questId };
		});

		if (temQuestStatus) {
			mutate({
				variables: {
					questId: questId,
					questStatus: TaskStatus.Read,
				},
				onCompleted: () => {
					setQuestStatus(false);
					onUpdate(rowIndex, { questStatus: TaskStatus.Read });
					if (isAssignee) {
						setUserData((prev) => {
							return { ...prev, questsCount: prev.questsCount > 0 ? prev.questsCount - 1 : 0 };
						});
					}
				},
			});
		}
	}

	function handleDismiss() {
		!isRunning ? start() : restart(expiryTimestamp, false);
	}

	return (
		<Accordion
			onChange={(e) => toggleExpanded(e, expandedQuest.questId)}
			expanded={expandedQuest.questId === questId}
			TransitionProps={{ unmountOnExit: true }}
			style={{ borderRadius: "4px" }}
			elevation={0}
		>
			<AccordionSummary style={rowStyle(rowIndex, isVisible || expandedQuest.questId === questId)} className={accordionSummary}>
				<Box
					display="flex"
					alignItems={"center"}
					justifyContent="space-between"
					flexWrap={"nowrap"}
					className={task}
					style={{
						fontWeight: temQuestStatus && questCompleted === QuestCompletionStatus.Active ? 600 : 400,
						width: "100%",
					}}
				>
					<Box display="flex" alignItems={"center"} style={{ width: "50%" }}>
						<span
							style={{
								opacity: temQuestStatus && isAssignee && questCompleted === QuestCompletionStatus.Active ? 1 : 0,
							}}
							className={isReadDot}
						/>

						<Box style={{ maxWidth: "90%" }}>{formatQuestContent(triggerEventId, content, t)}</Box>
					</Box>
					<Box className={goToTaskContainerWrap} style={{ visibility: isVisible ? "visible" : "hidden" }}>
						<Box
							onClick={(e) => goToQuestHandle(e, triggerEventId)}
							className={goToTaskContainer}
							style={{ marginRight: questCompleted !== QuestCompletionStatus.Active ? 40 : 0 }}
						>
							<Typography type="bold" style={{ marginLeft: "8px" }}>
								{t("go-to-quest")}
							</Typography>{" "}
							<IconButton>
								<CallMadeIcon style={{ width: 15 }} color="primary" />
							</IconButton>
						</Box>

						{questCompleted === QuestCompletionStatus.Active && (
							<IconButton className={iconButton} onClick={(e) => markQuestAsReadOrUnread(e)}>
								{temQuestStatus ? (
									<LyfeTooltip title={t("mark-as-read")}>
										<MailOpenIcon color="primary" />
									</LyfeTooltip>
								) : (
									<LyfeTooltip title={t("mark-as-unread")}>
										<MailClosedIcon color="primary" />
									</LyfeTooltip>
								)}
							</IconButton>
						)}
					</Box>
					<Box style={{ flexBasis: "18%", textAlign: "right" }}>
						<Typography type="inherit" variant="caption">
							{formatDateString(questDate, t, true)}
						</Typography>{" "}
					</Box>
					<IconButton className={iconQuest} onClick={(e) => handleOwnership(e)}>
						{!tempQuestOwner && !isAutoCompleted ? (
							<LyfeTooltip
								placement="right-end"
								title={questCompleted === QuestCompletionStatus.Active ? t("accept-quest") : ""}
							>
								<AddIcon style={{ width: 16, height: 16 }} color="primary" />
							</LyfeTooltip>
						) : (
							<Box
								onMouseEnter={() => {
									setShow(true);
								}}
								onMouseLeave={() => {
									setShow(false);
								}}
							>
								{!show ||
								questCompleted !== QuestCompletionStatus.Active ||
								(tempQuestOwner && tempQuestOwner.id && tempQuestOwner.id !== user.id) ||
								isAutoCompleted ? (
									<div>
										<LyfeAvatar
											isSystemAvatar={isAutoCompleted}
											toolTipTitle={`${t("quest")} ${t(getHoverText())} ${
												isAutoCompleted ? t("system") : getUserOwner()
											} `}
											userId={tempQuestOwner?.id}
											size="XSmall"
											isInsideButton={true}
											hasBadge={questCompleted !== QuestCompletionStatus.Active}
											firstName={`${isAutoCompleted ? "" : tempQuestOwner?.firstName?.substring(0, 1)}`}
											lastName={`${isAutoCompleted ? "" : tempQuestOwner?.lastName?.substring(0, 1)}`}
											badgeColor={questCompleted === QuestCompletionStatus.Dismissed ? "#5e6e7d" : "#038331"}
										/>
									</div>
								) : (
									<LyfeTooltip placement="right-end" title={t("leave-quest")}>
										<ClearIcon style={{ width: 16, height: 16 }} color="primary" />
									</LyfeTooltip>
								)}
							</Box>
						)}
					</IconButton>
				</Box>
			</AccordionSummary>
			<AccordionDetails style={{ backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#f5f8f9" }}>
				<Collapse in={expandedQuest.questId === questId} style={{ width: "100%" }}>
					<Box display="flex" flexDirection="column" flexWrap={"nowrap"} className={questContent}>
						<Box className={expandedQuestContent}>{formatExpansionQuestContent(triggerEventId, content, t)}</Box>
						<Divider />
						<Box style={{ marginTop: "15px", width: "20%" }} display="flex" justifyContent="start" alignItems="center">
							<Typography style={{ marginRight: 9, fontSize: 14, fontWeight: 600 }}>{t("dismiss-quest")}</Typography>
							<NotificationTooltip title={t("tooltip-dismiss")} placement="right">
								<InfoOutlined style={{ width: 14, height: 14 }} color="primary" />
							</NotificationTooltip>
						</Box>
						<Box display="flex" style={{ marginTop: "15px", width: "20%" }} justifyContent="space-between" flexWrap={"nowrap"}>
							<LyfeButton
								disabled={questCompleted !== QuestCompletionStatus.Active || isRunning}
								onClick={handleDismiss}
								style={{ marginRight: 16 }}
								variant="outlined"
								color="primary"
							>
								{t("dismiss")}
							</LyfeButton>
							{isRunning && handleRestart(seconds)}
							<Box display="flex" alignItems="center">
								{isRunning && <DeletionCountDown seconds={seconds} />}
								{isRunning && (
									<LyfeTooltip placement="right-end" title={t("undo")}>
										<IconButton style={{ marginLeft: "5px" }} onClick={handleClickDelete} size="small">
											<Undo color="primary" />
										</IconButton>
									</LyfeTooltip>
								)}
							</Box>
						</Box>
					</Box>
				</Collapse>
			</AccordionDetails>
		</Accordion>
	);
};

export default InboxQuestContent;
